import { format } from "date-fns";

import { announcement, misc } from "app/themes/colors";
import type { Mention, MentionTeam } from "services/types/mentions.type";

/** "Oct 5, 2:26 PM": when someone was notified or saw a mention. */
export const formatMentionMoment = (iso: string) =>
  format(new Date(iso), "MMM d, h:mm a");

/** Where a chart-note mention opens: the chart notes page expands and scrolls to the note. */
export const buildMentionChartNoteHref = (
  patientId: number,
  chartNoteId: number
) => `/patients/${patientId}/chart-notes?chart_note=${chartNoteId}`;

/**
 * Team @mentions (TEAM_MENTIONS_FEATURE) in rich text: chart notes and task descriptions.
 * (Task comments are plain text — see `mention-text.util.ts`.)
 *
 * A mention is markup in the HTML:
 *   <span class="suno-mention" contenteditable="false" data-mention-user-id="12">@Karen Ruiz</span>
 * Typing it notifies nobody. The writer clicks "Notify" under the editor, and only then does the
 * backend record a Mention and ping the person. So a mention is in one of three states:
 *   - pending:  in the text, not notified, not dismissed  → dashed pill + the Notify bar
 *   - notified: has a ChartNoteMention row               → solid pill
 *   - skipped:  the writer chose "Don't notify"           → plain pill, `data-mention-skip="true"`
 *
 * The pill is plain text everywhere else: print, PDF, fax and email all render "@Karen Ruiz".
 *
 * A task description has no Notify bar: saving the task notifies everyone whose pill is in it
 * (the backend parses the pills), so its editor uses the solid style (`MENTION_SOLID_CONTENT_STYLE`).
 */

export const MENTION_CLASS = "suno-mention";
export const MENTION_USER_ID_ATTR = "data-mention-user-id";
/**
 * A team pill (TEAMS_FEATURE): `<span class="suno-mention" data-mention-team-id="7">@Billing</span>`.
 * Sending it reaches every member; the first to take it owns it.
 */
export const MENTION_TEAM_ID_ATTR = "data-mention-team-id";
export const MENTION_SKIP_ATTR = "data-mention-skip";
export const MENTION_AUTOCOMPLETER_ID = "sunoMentions";
export const MENTION_TOOLBAR_BUTTON = "suno_mention_button";
export const MENTION_STATE_STYLE_ID = "suno-mention-state";

export type ParsedMention = {
  userId: number;
  name: string;
  skipped: boolean;
};

export type ParsedTeamMention = {
  teamId: number;
  name: string;
  skipped: boolean;
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export const buildMentionHtml = (userId: number, name: string) =>
  `<span class="${MENTION_CLASS}" contenteditable="false" ${MENTION_USER_ID_ATTR}="${userId}">@${escapeHtml(name)}</span>`;

export const buildTeamMentionHtml = (teamId: number, name: string) =>
  `<span class="${MENTION_CLASS}" contenteditable="false" ${MENTION_TEAM_ID_ATTR}="${teamId}">@${escapeHtml(name)}</span>`;

/**
 * Every distinct person mentioned in the note, in the order they first appear. A person mentioned
 * twice counts once; they are "skipped" only if every one of their pills was dismissed.
 */
export const parseMentions = (
  html: string | null | undefined
): ParsedMention[] => {
  if (!html || !html.includes(MENTION_CLASS)) return [];

  const doc = new DOMParser().parseFromString(html, "text/html");
  const byUser = new Map<number, ParsedMention>();
  doc
    .querySelectorAll<HTMLElement>(
      `span.${MENTION_CLASS}[${MENTION_USER_ID_ATTR}]`
    )
    .forEach((element) => {
      const userId = Number(element.getAttribute(MENTION_USER_ID_ATTR));
      if (!Number.isInteger(userId) || userId <= 0) return;
      const skipped = element.getAttribute(MENTION_SKIP_ATTR) === "true";
      const name = (element.textContent ?? "").replace(/^@/, "").trim();
      const existing = byUser.get(userId);
      if (existing) {
        existing.skipped = existing.skipped && skipped;
        return;
      }
      byUser.set(userId, { userId, name, skipped });
    });
  return Array.from(byUser.values());
};

/**
 * Every distinct team mentioned in the text, in the order they first appear (TEAMS_FEATURE).
 * Same rules as `parseMentions`: a team counts as skipped only if every one of its pills was.
 */
export const parseTeamMentions = (
  html: string | null | undefined
): ParsedTeamMention[] => {
  if (!html || !html.includes(MENTION_TEAM_ID_ATTR)) return [];

  const doc = new DOMParser().parseFromString(html, "text/html");
  const byTeam = new Map<number, ParsedTeamMention>();
  doc
    .querySelectorAll<HTMLElement>(
      `span.${MENTION_CLASS}[${MENTION_TEAM_ID_ATTR}]`
    )
    .forEach((element) => {
      const teamId = Number(element.getAttribute(MENTION_TEAM_ID_ATTR));
      if (!Number.isInteger(teamId) || teamId <= 0) return;
      const skipped = element.getAttribute(MENTION_SKIP_ATTR) === "true";
      const name = (element.textContent ?? "").replace(/^@/, "").trim();
      const existing = byTeam.get(teamId);
      if (existing) {
        existing.skipped = existing.skipped && skipped;
        return;
      }
      byTeam.set(teamId, { teamId, name, skipped });
    });
  return Array.from(byTeam.values());
};

/** Whether the HTML holds at least one mention pill, a person's or a team's (cheap check, no parsing). */
export const hasMentionPills = (html: string | null | undefined): boolean =>
  Boolean(
    html &&
      html.includes(MENTION_CLASS) &&
      (parseMentions(html).length || parseTeamMentions(html).length)
  );

/** Mentions typed into the note that nobody has notified or dismissed yet. */
export const getPendingMentions = (
  html: string | null | undefined,
  mentions: Mention[] | undefined,
  currentUserId?: number
): ParsedMention[] => {
  const notified = new Set((mentions ?? []).map((mention) => mention.user.id));
  return parseMentions(html).filter(
    (mention) =>
      !mention.skipped &&
      !notified.has(mention.userId) &&
      mention.userId !== currentUserId
  );
};

/** Teams typed into the note that nobody has sent to or dismissed yet (TEAMS_FEATURE). */
export const getPendingTeamMentions = (
  html: string | null | undefined,
  mentions: Mention[] | undefined
): ParsedTeamMention[] => {
  const sent = new Set(
    (mentions ?? [])
      .map((mention) => mention.team?.id)
      .filter((id): id is number => typeof id === "number")
  );
  return parseTeamMentions(html).filter(
    (team) => !team.skipped && !sent.has(team.teamId)
  );
};

/** The team ids among a note's mention rows (one row per member, all carrying the team). */
export const getNotifiedTeamIds = (mentions: Mention[] | undefined): number[] =>
  Array.from(
    new Set(
      (mentions ?? [])
        .map((mention) => mention.team?.id)
        .filter((id): id is number => typeof id === "number")
    )
  );

/** "Karen", "Karen and Kathy", "Karen, Kathy and 2 others" — first names keep the button short. */
export const formatMentionNames = (names: string[], maxShown = 2): string => {
  const firstNames = names.map((name) => name.split(" ")[0] || name);
  if (firstNames.length <= 1) return firstNames[0] ?? "";
  if (firstNames.length === 2) return `${firstNames[0]} and ${firstNames[1]}`;
  if (firstNames.length <= maxShown + 1) {
    return `${firstNames.slice(0, -1).join(", ")} and ${firstNames[firstNames.length - 1]}`;
  }
  const others = firstNames.length - maxShown;
  return `${firstNames.slice(0, maxShown).join(", ")} and ${others} others`;
};

/**
 * Base pill styles for the editor iframe. Pending is the default (dashed): the per-note state style
 * (`buildMentionStateCss`) turns notified people solid. Colours are the app's purple accents
 * (misc.purpleText / misc.highlightPurple) — the iframe cannot read the MUI theme.
 */
export const MENTION_CONTENT_STYLE = `
  .${MENTION_CLASS} {
    color: ${misc.purpleText};
    font-weight: 700;
    border-radius: 4px;
    padding: 0 3px;
    white-space: nowrap;
    border: 1.5px dashed ${misc.purpleText};
    background: transparent;
    cursor: default;
  }
  .${MENTION_CLASS}[${MENTION_SKIP_ATTR}="true"] {
    border: 1.5px solid transparent;
    background: ${misc.mutedPurple};
  }
  .${MENTION_CLASS}[${MENTION_TEAM_ID_ATTR}] {
    color: ${announcement.accent};
    border-color: ${announcement.accent};
  }
  .${MENTION_CLASS}[${MENTION_TEAM_ID_ATTR}][${MENTION_SKIP_ATTR}="true"] {
    border-color: transparent;
    background: ${announcement.background};
  }
`;

/**
 * Solid pills, for editors where saving is what notifies (task descriptions): there is no pending
 * state to show.
 */
export const MENTION_SOLID_CONTENT_STYLE = `
  .${MENTION_CLASS} {
    color: ${misc.purpleText};
    font-weight: 700;
    border-radius: 4px;
    padding: 0 3px;
    white-space: nowrap;
    background: ${misc.highlightPurple};
    cursor: default;
  }
  .${MENTION_CLASS}[${MENTION_TEAM_ID_ATTR}] {
    color: ${announcement.accent};
    background: ${announcement.background};
  }
`;

/**
 * Turns the pills of already-notified people (and already-sent teams) solid. Ids are numbers, so
 * this is injection-safe.
 */
export const buildMentionStateCss = (
  notifiedUserIds: number[],
  notifiedTeamIds: number[] = []
): string =>
  [
    ...notifiedUserIds
      .filter((id) => Number.isInteger(id))
      .map(
        (id) =>
          `.${MENTION_CLASS}[${MENTION_USER_ID_ATTR}="${id}"] { border: 1.5px solid transparent; background: ${misc.highlightPurple}; }`
      ),
    ...notifiedTeamIds
      .filter((id) => Number.isInteger(id))
      .map(
        (id) =>
          `.${MENTION_CLASS}[${MENTION_TEAM_ID_ATTR}="${id}"] { border: 1.5px solid transparent; background: ${announcement.background}; }`
      )
  ].join("\n");

/** Applies the notified/pending pill state inside a TinyMCE iframe without touching the note's HTML. */
export const applyMentionStateToEditorDocument = (
  doc: Document | null | undefined,
  notifiedUserIds: number[],
  notifiedTeamIds: number[] = []
) => {
  if (!doc?.head) return;
  let style = doc.getElementById(
    MENTION_STATE_STYLE_ID
  ) as HTMLStyleElement | null;
  if (!style) {
    style = doc.createElement("style");
    style.id = MENTION_STATE_STYLE_ID;
    doc.head.appendChild(style);
  }
  style.textContent = buildMentionStateCss(notifiedUserIds, notifiedTeamIds);
};

/** Marks every pill for `userIds` (and `teamIds`) as "Don't notify" in the note HTML. */
export const markMentionsSkipped = (
  html: string,
  userIds: number[],
  teamIds: number[] = []
): string => {
  if (!userIds.length && !teamIds.length) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const ids = new Set(userIds.map(String));
  const teams = new Set(teamIds.map(String));
  let changed = false;
  doc
    .querySelectorAll<HTMLElement>(
      `span.${MENTION_CLASS}[${MENTION_USER_ID_ATTR}], span.${MENTION_CLASS}[${MENTION_TEAM_ID_ATTR}]`
    )
    .forEach((element) => {
      const userId = element.getAttribute(MENTION_USER_ID_ATTR);
      const teamId = element.getAttribute(MENTION_TEAM_ID_ATTR);
      if (
        (userId !== null && ids.has(userId)) ||
        (teamId !== null && teams.has(teamId))
      ) {
        element.setAttribute(MENTION_SKIP_ATTR, "true");
        changed = true;
      }
    });
  return changed ? doc.body.innerHTML : html;
};

/** Where a mention's follow-up task opens: the deep link the task dialog reads (`?task=`). */
export const buildMentionTaskHref = (taskId: number) => `/?task=${taskId}`;

/**
 * Pill styles for mention HTML rendered in the app (outside an editor iframe), e.g. a task
 * description in the task view. Spread into the `sx` of the element that holds the HTML.
 */
export const MENTION_PILL_SX = {
  [`& .${MENTION_CLASS}`]: {
    color: misc.purpleText,
    fontWeight: 700,
    backgroundColor: misc.highlightPurple,
    borderRadius: "4px",
    px: "3px",
    whiteSpace: "nowrap"
  },
  // A team pill (TEAMS_FEATURE) is blue, so it never reads as a person.
  [`& .${MENTION_CLASS}[${MENTION_TEAM_ID_ATTR}]`]: {
    color: announcement.accent,
    backgroundColor: announcement.background
  }
} as const;

export type TeamGroup<T extends Mention> = {
  team: MentionTeam;
  rows: T[];
  seen: number;
  takenBy: { id: number; name: string } | null;
  taskId: number | null;
};

/** The rows reached through a team, grouped per team, in first-appearance order (TEAMS_FEATURE). */
export const groupTeamMentions = <T extends Mention>(
  mentions: T[],
  getTaskId: (mention: T) => number | null | undefined
): TeamGroup<T>[] => {
  const groups = new Map<number, TeamGroup<T>>();
  mentions.forEach((mention) => {
    if (!mention.team) return;
    let group = groups.get(mention.team.id);
    if (!group) {
      group = {
        team: mention.team,
        rows: [],
        seen: 0,
        takenBy: null,
        taskId: null
      };
      groups.set(mention.team.id, group);
    }
    group.rows.push(mention);
    if (mention.seen_at) group.seen += 1;
    if (mention.taken_by && !group.takenBy) {
      group.takenBy = mention.taken_by;
      group.taskId = getTaskId(mention) ?? null;
    }
  });
  return Array.from(groups.values());
};
